import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    WorkspaceSwitcher
} from "./brand";
import {
    type NCoreUIKitIcon,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    AvatarGroup,
    Breadcrumb,
    Separator,
    TextInput,
    Timeline,
    BarChart,
    Progress,
    CheckBox,
    Stepper,
    Sidebar,
    Avatar,
    Button,
    Badge,
    Chart,
    Card,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    LayoutDashboard,
    CircleCheck,
    TrendingUp,
    Calendar,
    Folder,
    Search,
    Share2,
    Users,
    Plus,
    Bell
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type Priority = "high" | "mid" | "low";

type DueKind = "late" | "soon" | "normal";

type TaskRow = {
    priority: Priority;
    assignee: string;
    dueKind: DueKind;
    title: string;
    id: string;
    due: Date;
};

type TaskGroup = {
    rows: Array<TaskRow>;
    title: string;
    id: string;
};

type ActivityRow = {
    detail: string;
    action: string;
    person: string;
    time: Date;
    id: string;
};

const LayoutDashboardIcon = createKitIcon(LayoutDashboard);

const TrendingUpIcon = createKitIcon(TrendingUp);

const CircleCheckIcon = createKitIcon(CircleCheck);

const CalendarIcon = createKitIcon(Calendar);

const FolderIcon = createKitIcon(Folder);

const SearchIcon = createKitIcon(Search);

const Share2Icon = createKitIcon(Share2);

const UsersIcon = createKitIcon(Users);

const PlusIcon = createKitIcon(Plus);

const BellIcon = createKitIcon(Bell);

const TEAM = [
    "ed",
    "mk",
    "za",
    "co",
    "al",
    "bt",
    "sy",
    "ke"
];

const TABS = [
    "overview",
    "tasks",
    "timeline",
    "files"
];

const FAVORITES = [
    {
        colorIndex: 1,
        id: "mobile"
    },
    {
        colorIndex: 3,
        id: "winter"
    },
    {
        colorIndex: 4,
        id: "brand"
    }
] as const;

const WEEK = [
    {
        day: "dayMon",
        done: 4
    },
    {
        day: "dayTue",
        done: 6
    },
    {
        day: "dayWed",
        done: 5
    },
    {
        day: "dayThu",
        done: 8
    },
    {
        day: "dayFri",
        done: 7
    },
    {
        day: "daySat",
        done: 2
    },
    {
        day: "daySun",
        done: 1
    }
];

const MILESTONES = [
    "discovery",
    "design",
    "build",
    "test",
    "launch"
];

const TASK_GROUPS: Array<TaskGroup> = [
    {
        rows: [
            {
                assignee: "blocks.project01.personMk",
                title: "blocks.project01.task1",
                due: new Date(2026, 10, 2),
                priority: "high",
                dueKind: "soon",
                id: "task1"
            },
            {
                assignee: "blocks.project01.personCo",
                title: "blocks.project01.task2",
                due: new Date(2026, 9, 28),
                priority: "high",
                dueKind: "late",
                id: "task2"
            },
            {
                assignee: "blocks.project01.personZa",
                title: "blocks.project01.task3",
                due: new Date(2026, 10, 6),
                dueKind: "normal",
                priority: "mid",
                id: "task3"
            },
            {
                assignee: "blocks.project01.personEd",
                title: "blocks.project01.task4",
                due: new Date(2026, 10, 9),
                dueKind: "normal",
                priority: "low",
                id: "task4"
            }
        ],
        title: "blocks.project01.groupActive",
        id: "active"
    },
    {
        rows: [
            {
                assignee: "blocks.project01.personAl",
                title: "blocks.project01.task5",
                due: new Date(2026, 10, 12),
                dueKind: "normal",
                priority: "mid",
                id: "task5"
            },
            {
                assignee: "blocks.project01.personMk",
                title: "blocks.project01.task6",
                due: new Date(2026, 10, 14),
                dueKind: "normal",
                priority: "mid",
                id: "task6"
            },
            {
                assignee: "blocks.project01.personZa",
                title: "blocks.project01.task7",
                due: new Date(2026, 11, 2),
                dueKind: "normal",
                priority: "low",
                id: "task7"
            },
            {
                assignee: "blocks.project01.personEd",
                title: "blocks.project01.task8",
                due: new Date(2026, 11, 8),
                dueKind: "normal",
                priority: "low",
                id: "task8"
            }
        ],
        title: "blocks.project01.groupNext",
        id: "next"
    }
];

const ACTIVITY: Array<ActivityRow> = [
    {
        detail: "blocks.project01.act1Detail",
        person: "blocks.project01.personMk",
        time: new Date(2026, 10, 1, 14, 12),
        action: "blocks.project01.act1",
        id: "act1"
    },
    {
        detail: "blocks.project01.act2Detail",
        person: "blocks.project01.personZa",
        time: new Date(2026, 10, 1, 11, 40),
        action: "blocks.project01.act2",
        id: "act2"
    },
    {
        detail: "blocks.project01.act3Detail",
        person: "blocks.project01.personEd",
        time: new Date(2026, 10, 1, 9, 5),
        action: "blocks.project01.act3",
        id: "act3"
    },
    {
        detail: "blocks.project01.act4Detail",
        person: "blocks.project01.personCo",
        time: new Date(2026, 9, 31, 17, 48),
        action: "blocks.project01.act4",
        id: "act4"
    },
    {
        detail: "blocks.project01.act5Detail",
        person: "blocks.project01.personAl",
        time: new Date(2026, 9, 31, 13, 20),
        action: "blocks.project01.act5",
        id: "act5"
    },
    {
        detail: "blocks.project01.act6Detail",
        person: "blocks.project01.personMk",
        time: new Date(2026, 9, 30, 16, 2),
        action: "blocks.project01.act6",
        id: "act6"
    }
];

const STATS = [
    {
        id: "completed",
        color: "high",
        value: 26
    },
    {
        id: "inProgress",
        color: "high",
        value: 11
    },
    {
        color: "danger",
        id: "overdue",
        value: 5
    }
] as const;

const PRIORITY_TYPES = {
    high: "danger",
    mid: "warning",
    low: "neutral"
} as const;

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    summary: {
        justifyContent: "space-between",
        alignItems: "baseline",
        flexDirection: "row"
    },
    row: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    bar: {
        alignItems: "center",
        flexDirection: "row"
    },
    titleRow: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    split: {
        flexDirection: "row"
    },
    stats: {
        flexDirection: "row"
    },
    tabs: {
        alignSelf: "stretch"
    },
    search: {
        flexShrink: 1
    },
    flex: {
        minWidth: 0,
        flex: 1
    },
    taskTitle: {
        minWidth: 0,
        flex: 1
    },
    card: {
        minWidth: 0,
        flex: 1
    },
    content: {
        minWidth: 0
    },
    stat: {
        flex: 1
    }
});

const handlePress = () => undefined;

const renderDot = (color: string, size: number, radius: number) => <View
    style={{
        backgroundColor: color,
        borderRadius: radius,
        height: size,
        width: size
    }}
/>;

const Project01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        iconSizes,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        activePage,
        setActivePage
    ] = useState("projects");

    const [
        activeTab,
        setActiveTab
    ] = useState("overview");

    const [
        doneIds,
        setDoneIds
    ] = useState<Array<string>>([]);

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isRoomy = width >= shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth;

    const dueFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short",
        day: "numeric"
    });

    const handleTabChange = (value: string) => setActiveTab(value);

    const handlePage = (page: string) => () => setActivePage(page);

    const handleTaskToggle = (id: string) => () => setDoneIds(
        doneIds.includes(id) ? doneIds.filter((key) => key !== id) : [
            ...doneIds,
            id
        ]
    );

    const renderNavItem = (page: string, icon: NCoreUIKitIcon, title: string, count?: number) => <Sidebar.MenuItem
        key={page}
    >
        <Sidebar.MenuButton
            isActive={activePage === page}
            onPress={handlePage(page)}
            title={title}
            icon={icon}
        />
        {
            count === undefined ?
                null
            :
                <Sidebar.MenuBadge
                    count={count}
                />
        }
    </Sidebar.MenuItem>;

    const renderFavorite = (favorite: typeof FAVORITES[number]) => <Sidebar.MenuItem
        key={favorite.id}
    >
        <Sidebar.MenuButton
            renderIcon={() => renderDot(colors.chart.categorical[favorite.colorIndex], iconSizes.small / 2, radiuses.full)}
            title={t(`blocks.project01.favorite_${favorite.id}`)}
            isActive={activePage === favorite.id}
            onPress={handlePage(favorite.id)}
        />
    </Sidebar.MenuItem>;

    const renderTask = (task: TaskRow) => <View
        style={[
            stylesheet.row,
            {
                paddingVertical: spaces.spacingSm,
                gap: spaces.spacingSm
            }
        ]}
        key={task.id}
    >
        <View
            style={stylesheet.taskTitle}
        >
            <CheckBox
                isChecked={doneIds.includes(task.id)}
                onPress={handleTaskToggle(task.id)}
                spreadBehaviour="stretch"
                title={t(task.title)}
            />
        </View>
        <Badge
            title={t(`blocks.project01.priority_${task.priority}`)}
            type={PRIORITY_TYPES[task.priority]}
            spreadBehaviour="free"
        />
        <Badge
            type={task.dueKind === "late" ? "danger" : task.dueKind === "soon" ? "warning" : "neutral"}
            variant={task.dueKind === "normal" ? "outline" : "tonal"}
            title={dueFormat.format(task.due)}
            spreadBehaviour="free"
        />
        <Avatar
            title={t(task.assignee)}
            size="xSmall"
        />
    </View>;

    const renderGroup = (group: TaskGroup) => <View
        key={group.id}
    >
        <View
            style={[
                stylesheet.row,
                {
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="labelSmallSize"
                color="mid"
            >
                {t(group.title)}
            </Text>
            <Badge
                title={String(group.rows.length)}
                spreadBehaviour="free"
                type="neutral"
                size="small"
            />
        </View>
        {
            group.rows.map((task) => <View
                key={task.id}
            >
                <Separator/>
                {renderTask(task)}
            </View>)
        }
    </View>;

    const renderActivity = (item: ActivityRow) => <Timeline.Item
        description={t(item.detail)}
        title={<>
            <Timeline.Emphasis>
                {t(item.person)}
            </Timeline.Emphasis>
            {` ${t(item.action)}`}
        </>}
        avatar={{
            title: t(item.person)
        }}
        time={item.time}
        key={item.id}
    />;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            collapseBehaviour={isRoomy ? "offcanvas" : "icon"}
            title={t("blocks.project01.navTitle")}
        >
            <Sidebar.Header>
                <WorkspaceSwitcher
                    onPress={handlePress}
                />
            </Sidebar.Header>
            <Sidebar.Content>
                <Sidebar.Group>
                    <Sidebar.Menu>
                        {renderNavItem("home", LayoutDashboardIcon, t("blocks.project01.navHome"))}
                        {renderNavItem("projects", FolderIcon, t("blocks.project01.navProjects"), 12)}
                        {renderNavItem("tasks", CircleCheckIcon, t("blocks.project01.navTasks"), 9)}
                        {renderNavItem("calendar", CalendarIcon, t("blocks.project01.navCalendar"))}
                        {renderNavItem("team", UsersIcon, t("blocks.project01.navTeam"))}
                    </Sidebar.Menu>
                </Sidebar.Group>
                <Sidebar.Group
                    title={t("blocks.project01.favorites")}
                >
                    <Sidebar.Menu>
                        {FAVORITES.map(renderFavorite)}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer>
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            renderIcon={() => <Avatar
                                title={t("blocks.project01.userName")}
                                size="xSmall"
                            />}
                            subTitle={t("blocks.project01.userEmail")}
                            title={t("blocks.project01.userName")}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.bar,
                    {
                        paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        paddingVertical: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Sidebar.Trigger/>
                <View
                    style={stylesheet.flex}
                >
                    {
                        isCompact ?
                            null
                        :
                            <Breadcrumb
                                accessibilityLabel={t("blocks.project01.breadcrumb")}
                                compactBehaviour="collapse"
                            >
                                <Breadcrumb.List>
                                    <Breadcrumb.Item>
                                        <Breadcrumb.Link
                                            title={t("blocks.project01.navProjects")}
                                            onPress={handlePress}
                                        />
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item>
                                        <Breadcrumb.Page
                                            title={t("blocks.project01.projectName")}
                                        />
                                    </Breadcrumb.Item>
                                </Breadcrumb.List>
                            </Breadcrumb>
                    }
                </View>
                {
                    isCompact ?
                        null
                    :
                        <TextInput
                            style={[
                                stylesheet.search,
                                {
                                    width: shapeConfigs.fieldMinWidth + shapeConfigs.fieldMinWidth / 2
                                }
                            ]}
                            placeholder={t("blocks.project01.searchPlaceholder")}
                            accessibilityLabel={t("blocks.project01.search")}
                            spreadBehaviour="free"
                            icon={SearchIcon}
                            size="small"
                        />
                }
                <Badge.Indicator
                    type="danger"
                >
                    <Button
                        accessibilityLabel={t("blocks.project01.notifications")}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        variant="ghost"
                        icon={BellIcon}
                        type="neutral"
                        size="small"
                    />
                </Badge.Indicator>
            </View>
            <Separator/>
            <View
                style={[
                    stylesheet.content,
                    {
                        padding: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.header,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.flex,
                            {
                                minWidth: shapeConfigs.fieldMinWidth,
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.titleRow,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Text
                                variant="headlineMediumSize"
                                accessibilityRole="header"
                                color="high"
                            >
                                {t("blocks.project01.projectName")}
                            </Text>
                            <Badge
                                title={t("blocks.project01.onTrack")}
                                spreadBehaviour="free"
                                icon={CircleCheckIcon}
                                type="success"
                            />
                        </View>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.project01.projectLead")}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <AvatarGroup
                            avatars={TEAM.map((key) => ({
                                title: t(`blocks.project01.member_${key}`)
                            }))}
                            size="xSmall"
                            max={5}
                        />
                        <Button
                            title={t("blocks.project01.share")}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            variant="outline"
                            icon={Share2Icon}
                            type="neutral"
                        />
                        <Button
                            title={t("blocks.project01.addTask")}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            variant="filled"
                            icon={PlusIcon}
                            type="primary"
                        />
                    </View>
                </View>
                <Tabs
                    onValueChange={handleTabChange}
                    style={stylesheet.tabs}
                    value={activeTab}
                    variant="line"
                >
                    <Tabs.List
                        title={t("blocks.project01.tabsTitle")}
                    >
                        {
                            TABS.map((tab) => <Tabs.Trigger
                                count={tab === "tasks" ? 42 : undefined}
                                title={t(`blocks.project01.tab_${tab}`)}
                                value={tab}
                                key={tab}
                            />)
                        }
                    </Tabs.List>
                </Tabs>
                <Card
                    variant="outline"
                >
                    <Card.Header>
                        <Card.Title>
                            {t("blocks.project01.milestones")}
                        </Card.Title>
                        <Card.Action>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.project01.milestonesSummary")}
                            </Text>
                        </Card.Action>
                    </Card.Header>
                    <Card.Content>
                        <Stepper
                            steps={MILESTONES.map((key) => ({
                                description: t(`blocks.project01.milestone_${key}_dates`),
                                title: t(`blocks.project01.milestone_${key}`),
                                key
                            }))}
                            accessibilityLabel={t("blocks.project01.milestones")}
                            activeIndex={2}
                        />
                    </Card.Content>
                </Card>
                <View
                    style={[
                        isCompact ? undefined : stylesheet.split,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Card
                        style={stylesheet.card}
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.project01.progress")}
                            </Card.Title>
                            <Card.Description>
                                {t("blocks.project01.progressLead")}
                            </Card.Description>
                            <Card.Action>
                                <Badge
                                    title={t("blocks.project01.weekDelta")}
                                    spreadBehaviour="free"
                                    icon={TrendingUpIcon}
                                    type="success"
                                />
                            </Card.Action>
                        </Card.Header>
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingMd
                                }}
                            >
                                <View
                                    style={stylesheet.summary}
                                >
                                    <Text
                                        variant="headlineMediumSize"
                                        color="high"
                                    >
                                        {t("blocks.project01.percent")}
                                    </Text>
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.project01.target")}
                                    </Text>
                                </View>
                                <Progress
                                    title={t("blocks.project01.overallProgress")}
                                    type="primary"
                                    value={62}
                                >
                                    <Progress.Track/>
                                </Progress>
                                <Separator/>
                                <View
                                    style={[
                                        stylesheet.stats,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    {
                                        STATS.map((stat) => <View
                                            style={stylesheet.stat}
                                            key={stat.id}
                                        >
                                            <Text
                                                variant="bodySmallSize"
                                                color="mid"
                                            >
                                                {t(`blocks.project01.stat_${stat.id}`)}
                                            </Text>
                                            <Text
                                                variant="headlineSmallSize"
                                                color={stat.color}
                                            >
                                                {String(stat.value)}
                                            </Text>
                                        </View>)
                                    }
                                </View>
                            </View>
                        </Card.Content>
                    </Card>
                    <Chart
                        config={{
                            done: {
                                title: t("blocks.project01.completedTasks"),
                                colorIndex: 1
                            }
                        }}
                        description={t("blocks.project01.weekLead")}
                        note={t("blocks.project01.weekTotal")}
                        title={t("blocks.project01.thisWeek")}
                        spreadBehaviour="stretch"
                        isAnimatedOnMount={true}
                        style={stylesheet.card}
                    >
                        <BarChart
                            data={WEEK.map((row) => ({
                                day: t(`blocks.project01.${row.day}`),
                                done: row.done
                            }))}
                            categoryTitle={t("blocks.project01.day")}
                            labelBehaviour="extremes"
                            categoryKey="day"
                            plotSize="small"
                            seriesKeys={[
                                "done"
                            ]}
                        />
                    </Chart>
                </View>
                <View
                    style={[
                        isRoomy ? stylesheet.split : undefined,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Card
                        style={stylesheet.card}
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.project01.tasks")}
                            </Card.Title>
                            <Card.Action>
                                <Button
                                    title={t("blocks.project01.viewAll")}
                                    spreadBehaviour="free"
                                    onPress={handlePress}
                                    variant="link"
                                    type="primary"
                                    size="small"
                                />
                            </Card.Action>
                        </Card.Header>
                        <Card.Content>
                            {TASK_GROUPS.map(renderGroup)}
                        </Card.Content>
                    </Card>
                    <Card
                        style={[
                            stylesheet.card,
                            isRoomy ? {
                                maxWidth: shapeConfigs.cardWidth
                            } : undefined
                        ]}
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.project01.activity")}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Timeline
                                accessibilityLabel={t("blocks.project01.activity")}
                                timeBehaviour="absolute"
                                size="small"
                            >
                                {ACTIVITY.map(renderActivity)}
                            </Timeline>
                        </Card.Content>
                    </Card>
                </View>
            </View>
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Project01;
